import { formatDate } from "../utils/dateUtils";
import { initialLetter } from "../utils/idUtils";
import { currencySymbol, convertPriceForUser } from "../utils/currencyUtils";

export default function GiftListScreen({
  lang, darkMode, user, userProfile,
  viewingMember, viewingWishlist, setViewingWishlist,
  currentFamily, isMyProfile,
  showShareMenu, setShowShareMenu,
  wishlistImagesLoading, exchangeRates,
  profileLink, giftSortOrder, setGiftSortOrder,
  visibleGiftsCount, setVisibleGiftsCount,
  cameFromProfilePicker, setCameFromProfilePicker,
  setScreen, setActiveTab, setViewingMember,
  setGiftForm, setEditingManaged, setCooperateModal, setCooperateAmount,
  tr, convertPrice, makeLongPress,
  deleteGift, editGift, commitGift, uncommitGift,
  copyGiftToMyList, generatePDF, setPrimaryWishlistId,
  deleteManagedMember, showToast,
  useRealName,
  budgetData,
  userCurrency,
}) {
  const fid = currentFamily?.id;
  const displayName = (useRealName || !fid || !viewingMember?.groupNicknames?.[fid])
    ? (viewingMember?.name || "")
    : viewingMember.groupNicknames[fid];

  const renderGift = function(g) {
    const showPurchased = (g.purchased || (g.committedBy && g.committedBy !== user?.uid)) && !isMyProfile;
    return (
      <div key={g.id} className={"tarjeta" + (showPurchased ? " comprado" : "") + (!g.photoUrl ? " tarjeta-sin-foto" : "")} {...(isMyProfile ? makeLongPress(function() {
        if (window.confirm(lang === "es" ? "¿Eliminar " + (g.name || "este regalo") + "?" : "Delete " + (g.name || "this gift") + "?")) deleteGift(g.id);
      }) : {})}>

        {/* ── Imagen con botón editar flotando ── */}
        {g.photoUrl
          ? <div
              className="tarjeta-img-wrap"
              style={{ position: "relative" }}
              ref={function(el) { if (el) { var img = el.querySelector('img'); if (img && img.complete) el.classList.add('tarjeta-img-loaded'); } }}>
              <div className="tarjeta-img-skeleton" />
              <img
                src={g.photoUrl}
                alt={g.name || "Gift"}
                className="tarjeta-img"
                loading="lazy"
                style={{ width: "100%", borderRadius: "14px 14px 0 0" }}
                onLoad={function(e) { e.target.parentNode.classList.add('tarjeta-img-loaded'); }}
                onError={function(e) { e.target.style.display = "none"; e.target.parentNode.classList.add('tarjeta-img-loaded'); }}
              />
              {/* Botón editar flotando sobre la foto */}
              {isMyProfile && (
                <button
                  onClick={function() { editGift(g); }}
                  style={{ position: "absolute", top: 8, right: 8, background: "rgba(121,120,120,0)", border: "none", borderRadius: 20, padding: "4px 10px", fontSize: 12, cursor: "pointer", color: "#383636d6" }}>
                  ...
                </button>
              )}
            </div>
          : null
        }

        {/* ── Cuerpo de la tarjeta ── */}
        <div className="tarjeta-cuerpo">
          {!showPurchased && g.link && <a href={g.link} target="_blank" rel="noopener noreferrer" className="tarjeta-link">{tr("viewProduct")}</a>}
          {g.name && <div className="tarjeta-nombre">{g.name}</div>}

          {/* Precio convertido a la moneda del usuario */}
          {g.price && (function() {
            const converted = convertPriceForUser(g.price, g.currency || "USD", userProfile, exchangeRates);
            if (!converted) return null;
            return <div style={{ fontSize: 13, fontWeight: 600, color: "#d4a853", marginBottom: 4 }}>{converted.sym}{converted.val.toFixed(2)}</div>;
          })()}

          {g.description && <div className="tarjeta-desc">{g.description}</div>}
          {showPurchased && <span className="comprado-badge">{tr("alreadyPurchased")}</span>}

          {/* Barra de cooperaciones */}
          {(g.cooperations && g.cooperations.length > 0) && (function() {
            const giftSym = currencySymbol(g.currency || "USD");
            const totalCooped = g.cooperations.reduce(function(acc, c) { return acc + (parseFloat(c.amount) || 0); }, 0);
            const goalPrice = parseFloat(g.price) || 0;
            const pct = goalPrice > 0 ? Math.min(100, (totalCooped / goalPrice) * 100) : 0;
            return (
              <div style={{ marginTop: 6, marginBottom: 4 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#aaa", marginBottom: 3 }}>
                  <span>💸 {giftSym}{totalCooped.toFixed(0)} {goalPrice > 0 && "/ " + giftSym + goalPrice.toFixed(0)}</span>
                  {goalPrice > 0 && <span>{Math.round(pct)}%</span>}
                </div>
                {goalPrice > 0 && (
                  <div style={{ height: 5, background: darkMode ? "#2e2e2e" : "#f0ece6", borderRadius: 4, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: pct + "%", background: pct >= 100 ? "#27ae60" : "#d4a853", borderRadius: 4, transition: "width 0.4s" }} />
                  </div>
                )}
              </div>
            );
          })()}

          {/* Botones de acción */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
            {!user && (
              <button onClick={function() { setScreen("login"); }} style={{ display: "flex", alignItems: "center", gap: 4, background: darkMode ? "#2a2a2a" : "#f0ece6", border: "none", borderRadius: 20, padding: "5px 10px", fontSize: 11, cursor: "pointer", color: darkMode ? "#e8e2d9" : "#555" }}>
                🔑 {tr("signInToBuy")}
              </button>
            )}
            {user && !isMyProfile && !g.purchased && !g.committedBy && (g.photoUrl || g.price) && (
              <button onClick={function() { commitGift(viewingWishlist.id, g.id, g, false); }} style={{ display: "flex", alignItems: "center", gap: 4, background: "#56c47d", border: "none", borderRadius: 20, padding: "5px 10px", fontSize: 11, cursor: "pointer", color: "#fff", fontWeight: 600 }}>
                ☝️ {lang === "es" ? "¡Yo lo regalo!" : lang === "fi" ? "Minä lahjoitan" : "I'll gift this"}
              </button>
            )}
            {user && g.committedBy === user.uid && !g.purchased && (
              <button onClick={async function() {
                await uncommitGift({ wishlistId: viewingWishlist.id, giftId: g.id });
                setViewingWishlist(function(wl) {
                  if (!wl) return wl;
                  return { ...wl, gifts: wl.gifts.map(function(gg) {
                    if (gg.id !== g.id) return gg;
                    const copy = Object.assign({}, gg);
                    delete copy.committedBy; copy.purchased = false; copy.purchasedBy = null;
                    return copy;
                  })};
                });
              }} style={{ display: "flex", alignItems: "center", gap: 4, background: "transparent", border: "1px solid #e67e22", borderRadius: 20, padding: "5px 10px", fontSize: 11, cursor: "pointer", color: "#e67e22" }}>
                ✕ {tr("unmark")}
              </button>
            )}
            {user && !isMyProfile && !g.purchased && (
              <button
                onClick={function() { setCooperateAmount((g.cooperations || []).find(function(c) { return c.uid === user.uid; })?.amount || ""); setCooperateModal({ gift: g, wishlistId: viewingWishlist.id, ownerMember: viewingMember }); }}
                title={lang === "es" ? "Cooperar" : lang === "fi" ? "Osallistua" : "Contribute"}
                style={{ display: "flex", alignItems: "center", justifyContent: "center", background: "transparent", border: "none", borderRadius: 20, padding: "4px 8px", cursor: "pointer", lineHeight: 1 }}>
                <svg width="26" height="26" viewBox="0 0 110 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="8" y="52" width="62" height="44" rx="5" fill="#8e44ad" stroke="#6c3483" strokeWidth="3"/>
                  <rect x="4" y="40" width="70" height="16" rx="4" fill="#9b59b6" stroke="#6c3483" strokeWidth="3"/>
                  <rect x="33" y="40" width="10" height="56" fill="#d4a853"/>
                  <rect x="4" y="47" width="70" height="8" fill="#d4a853"/>
                  <path d="M38 40 C26 24 4 22 12 36 C17 46 34 44 38 40Z" fill="#d4a853"/>
                  <path d="M38 40 C50 24 72 22 64 36 C59 46 42 44 38 40Z" fill="#d4a853"/>
                  <circle cx="80" cy="28" r="26" fill="#FFD93D" stroke="#C8A000" strokeWidth="3"/>
                  <circle cx="80" cy="28" r="18" fill="#FFC200" stroke="#C8A000" strokeWidth="2"/>
                  <text x="80" y="35" textAnchor="middle" fill="#7A5800" fontSize="20" fontWeight="900" fontFamily="Arial, sans-serif">$</text>
                </svg>
              </button>
            )}
            {user && !isMyProfile && !g.purchased && (
              <button
                title={lang === "es" ? "Quiero esto también" : "I want this too"}
                onClick={function() { copyGiftToMyList(g, viewingMember, viewingWishlist); }}
                style={{ background: "transparent", border: "none", borderRadius: 20, padding: "5px 6px", fontSize: 18, cursor: "pointer", lineHeight: 1 }}>
                🤩
              </button>
            )}
            {/* Botón editar para regalos SIN foto */}
            {isMyProfile && !g.photoUrl && (
              <button
                onClick={function() { editGift(g); }}
                style={{ display: "flex", alignItems: "center", gap: 4, background: "transparent", border: "1px solid " + (darkMode ? "#444" : "#e0dbd2"), borderRadius: 20, padding: "5px 10px", fontSize: 11, cursor: "pointer", color: darkMode ? "#aaa" : "#777" }}>
                ... {tr("edit")}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div onClick={function(e) { if (showShareMenu) setShowShareMenu(false); }}>
      <div className="perfil-header">
        <div className="perfil-avatar" style={{ background: viewingMember.color || "#9b59b6", boxShadow: "0 0 0 4px " + (viewingMember.color || "#9b59b6") + "33" }}>
          {initialLetter(viewingMember.name)}
        </div>
        <div className="perfil-info">
          <h2>{displayName} {currentFamily && currentFamily.leaderId === viewingMember.id && "👑"}</h2>
          <p>{viewingWishlist.gifts.length} {tr("gifts")} {viewingMember.birthday && "· 🎂 " + formatDate(viewingMember.birthday)}</p>
          {viewingMember.isManaged && user && viewingMember.managedBy === user.uid && (
            <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
              <button className="btn btn-gris btn-sm" onClick={function() { setEditingManaged({ id: viewingMember.id, name: viewingMember.name, color: viewingMember.color || "#e67e22" }); }}>{tr("editManaged")}</button>
              <button className="btn btn-rojo btn-sm" onClick={function() { deleteManagedMember(viewingMember.id); }}>{tr("deleteManaged")}</button>
            </div>
          )}
        </div>
        <div className="perfil-acciones" style={{ position: "relative" }}>
          <button id="tutorial-share" className="btn btn-sm" style={{ background: "#d4a853", color: "#fff", border: "none", fontSize: 16, padding: "7px 12px" }} onClick={function(e) { e.stopPropagation(); setShowShareMenu(function(v) { return !v; }); }} title="Compartir">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" style={{ display: "inline-block", verticalAlign: "middle" }}><path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z"/></svg>
          </button>
          {showShareMenu && (
            <div style={{ position: "absolute", top: "110%", left: 0, background: darkMode ? "#1e1e1e" : "#fff", border: "1px solid " + (darkMode ? "#2e2e2e" : "#e8e2d9"), borderRadius: 12, boxShadow: "0 4px 20px rgba(0,0,0,0.15)", zIndex: 50, minWidth: 180, overflow: "hidden" }}>
              <div style={{ padding: "12px 16px", cursor: "pointer", fontSize: 13, display: "flex", alignItems: "center", gap: 10, borderBottom: "1px solid " + (darkMode ? "#2e2e2e" : "#f0ece6"), color: darkMode ? "#e8e2d9" : "#1a1a1a" }}
                onClick={function(e) { e.stopPropagation(); navigator.clipboard?.writeText(profileLink); showToast(tr("linkCopied")); setShowShareMenu(false); }}>
                🔗 {tr("copy")} link
              </div>
            </div>
          )}
          {isMyProfile && <button id="tutorial-add-gift" className="btn btn-negro btn-sm" onClick={function() { setGiftForm({ name: "", link: "", description: "", price: "", currency: userProfile?.currency || "USD", photoUrl: "", photoFile: null, editId: null }); setScreen("add"); setTimeout(function() { const p = document.querySelector(".pantalla"); if (p) p.scrollTop = 0; }, 30); }}>{tr("addGift")}</button>}
          {currentFamily && (
            <button className="btn btn-gris btn-sm" onClick={function() {
              setShowShareMenu(false);
              setViewingMember(null);
              setViewingWishlist(null);
              if (cameFromProfilePicker) {
                setCameFromProfilePicker(false);
                setActiveTab("groups");
                setScreen("family");
              } else {
                setScreen("family");
              }
            }}>{tr("back")}</button>
          )}
        </div>
      </div>

      {budgetData && budgetData.budget > 0 && !isMyProfile && currentFamily && (function() {
        const sym = currencySymbol(userCurrency);
        const usedAmt = budgetData.committed + budgetData.spent;
        const pct = Math.min(100, (usedAmt / budgetData.budget) * 100);
        const barColor = pct > 90 ? "#c0392b" : pct > 70 ? "#e67e22" : "#d4a853";
        const amtColor = pct > 90 ? "#c0392b" : pct > 70 ? "#e67e22" : (darkMode ? "#e8e2d9" : "#1a1a1a");
        return (
          <div style={{ background: darkMode ? "#1e1e1e" : "#fff", border: "1px solid " + (darkMode ? "#2e2e2e" : "#e8e2d9"), borderRadius: 14, padding: "12px 16px", marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#d4a853", marginBottom: 6 }}>💰 {lang === "es" ? "Mi presupuesto" : lang === "fi" ? "Budjettini" : "My budget"}</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: amtColor }}>
              {sym}{usedAmt.toFixed(0)}
              <span style={{ fontSize: 12, color: "#999", fontWeight: 400 }}> / {sym}{budgetData.budget.toFixed(0)}</span>
            </div>
            <div style={{ marginTop: 8, height: 5, background: darkMode ? "#333" : "#f0ece6", borderRadius: 4, overflow: "hidden" }}>
              <div style={{ height: "100%", width: pct + "%", background: barColor, borderRadius: 4, transition: "width 0.4s" }} />
            </div>
          </div>
        );
      })()}

      {viewingWishlist.gifts.length === 0 ? (
        <div className="vacio">
          <div className="vacio-icono">🎁</div>
          <h3>{tr("emptyList")}</h3>
          <p>{isMyProfile ? tr("addFirst") : tr("noGifts")}</p>
          {isMyProfile && (
            <button className="btn btn-dorado" style={{ marginTop: 20 }} onClick={function() { setGiftForm({ name: "", link: "", description: "", price: "", currency: userProfile?.currency || "USD", photoUrl: "", photoFile: null, editId: null }); setScreen("add"); setTimeout(function() { var p = document.querySelector(".pantalla"); if (p) p.scrollTop = 0; }, 30); }}>{tr("addGift")}</button>
          )}
        </div>
      ) : (
        <>
          {/* ── Barra de controles: Amazon + Ordenar ── */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", marginBottom: 10, gap: 8 }}>

            {/* Botón Amazon — solo si la wishlu actual está en la lista de visibilidad */}
            {viewingMember?.amazonWishlist &&
             viewingWishlist?.id &&
             (viewingMember?.amazonWishlistVisibility || []).includes(viewingWishlist.id) && (
              <a
                href={viewingMember.amazonWishlist}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: "flex", alignItems: "center", gap: 5, background: "#FF9900", borderRadius: 20, padding: "6px 12px", fontSize: 12, fontWeight: 600, color: "#fff", textDecoration: "none" }}
              >
                📦 Amazon
              </a>
            )}

            {/* Selector de orden */}
            <select value={giftSortOrder} onChange={function(e) { setGiftSortOrder(e.target.value); setVisibleGiftsCount(20); }}
              style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #e0dbd2", background: darkMode ? "#2a2a2a" : "#faf8f5", color: darkMode ? "#e8e2d9" : "#1a1a1a", fontSize: 12, fontFamily: "'DM Sans', sans-serif", cursor: "pointer", outline: "none" }}>
              <option value="date">{tr("orderByDate")}</option>
              <option value="name">{tr("orderByName")}</option>
              <option value="price">{tr("orderByPrice")}</option>
            </select>
          </div>

          {(function() {
            const sorted = [...viewingWishlist.gifts].sort(function(a, b) {
              if (giftSortOrder === "name") return (a.name || "").localeCompare(b.name || "");
              if (giftSortOrder === "price") { const pa = parseFloat(a.price) || 0; const pb = parseFloat(b.price) || 0; return pa - pb; }
              return 0;
            });

            const isNameOnly = function(g) { return !g.photoUrl && !g.price && !g.link; };
            const nameOnlyGifts = sorted.filter(isNameOnly).sort(function(a, b) {
              return (a.name || "").localeCompare(b.name || "");
            });
            const normalGifts = sorted.filter(function(g) { return !isNameOnly(g); });

            const noteCard = nameOnlyGifts.length > 0 && (
              <div key="__note__" className="tarjeta" style={{ background: darkMode ? "#1e1e1e" : "#fff", border: "1px solid " + (darkMode ? "#2e2e2e" : "#e8e2d9"), borderRadius: 14, overflow: "hidden" }}>
                <div style={{ background: "#d4a853", padding: "10px 14px", display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 15 }}>🎁</span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: "#4a2e00" }}>
                    {lang === "es" ? "Siempre viene bien:" : lang === "fi" ? "Sopii aina:" : "Always a good gift:"}
                  </span>
                </div>
                <div style={{ padding: "8px 14px 12px" }}>
                  {nameOnlyGifts.map(function(g, i) {
                    return (
                      <div key={g.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", borderBottom: i < nameOnlyGifts.length - 1 ? "0.5px solid " + (darkMode ? "#2e2e2e" : "#e8e2d9") : "none" }}>
                        <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#d4a853", flexShrink: 0 }} />
                        <div style={{ flex: 1, display: "flex", alignItems: "baseline", gap: 6 }}>
                          <div style={{ fontSize: 13, color: darkMode ? "#e8e2d9" : "#1a1a1a" }}>{g.name}</div>
                          {g.description && <div style={{ fontSize: 11, color: "#aaa" }}>{g.description}</div>}
                        </div>
                        {isMyProfile && (
                          <button onClick={function() { editGift(g); }} style={{ marginLeft: "auto", background: "transparent", border: "none", fontSize: 12, cursor: "pointer", color: darkMode ? "#888" : "#aaa", padding: "2px 6px" }}>...</button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );

            return (
              <div style={{ paddingBottom: 8 }}>
                <div className={giftSortOrder === "date" ? "grilla" : "grilla-grid"}>
                  {normalGifts.slice(0, visibleGiftsCount).map(renderGift)}
                </div>
                {noteCard}
              </div>
            );
          })()}

          {viewingWishlist.gifts.length > visibleGiftsCount && (
            <div style={{ textAlign: "center", marginTop: 20 }}>
              <button className="btn btn-gris" onClick={function() { setVisibleGiftsCount(function(n) { return n + 20; }); }}>{tr("lazyLoadMore")} ({viewingWishlist.gifts.length - visibleGiftsCount} {tr("gifts")})</button>
            </div>
          )}

          <div style={{ display: "flex", gap: 8, marginTop: 28, marginBottom: 8 }}>
            {isMyProfile && (
              <button className="btn btn-negro" style={{ flex: 1 }} onClick={function() { setGiftForm({ name: "", link: "", description: "", price: "", currency: userProfile?.currency || "USD", photoUrl: "", photoFile: null, editId: null }); setScreen("add"); setTimeout(function() { var p = document.querySelector(".pantalla"); if (p) p.scrollTop = 0; }, 30); }}>{tr("addGift")}</button>
            )}
            <button className="btn btn-gris" style={{ flex: 1 }} onClick={function() {
              setShowShareMenu(false);
              setViewingMember(null);
              setViewingWishlist(null);
              if (cameFromProfilePicker) {
                setCameFromProfilePicker(false);
                setActiveTab("groups");
                setScreen("family");
              } else {
                setScreen("family");
              }
            }}>{tr("back")}</button>
          </div>
        </>
      )}
    </div>
  );
}
